import type {
  CreateFile,
  Project,
  ProjectFile,
  Revision,
  UpdateFile,
} from "../shared/contracts";

export class ProjectApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}
/** Public project contract used by the AEE frontend; storage remains behind the API. */
export function createProjectApi(
  baseUrl: string,
  transport: typeof fetch = fetch,
) {
  const base = baseUrl.replace(/\/$/, "");
  async function request(path: string, init?: RequestInit) {
    const response = await transport(`${base}${path}`, init);
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new ProjectApiError(
        response.status,
        body?.error?.code ?? "API_ERROR",
        body?.error?.message ?? "Request failed.",
      );
    }
    return response;
  }
  const path = (projectId: string, fileId?: string) =>
    `/api/projects/${encodeURIComponent(projectId)}/files${fileId ? `/${encodeURIComponent(fileId)}` : ""}`;
  const json = (method: string, body: unknown): RequestInit => ({
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return {
    async listProjects(): Promise<{ projects: Project[] }> {
      return (await request("/api/projects")).json();
    },
    async createProject(name: string, description = ""): Promise<{ project: Project }> {
      return (await request("/api/projects", json("POST", { name, description }))).json();
    },
    async setProjectStar(
      id: string,
      starred: boolean,
    ): Promise<{ starred: boolean }> {
      return (
        await request(
          `/api/projects/${encodeURIComponent(id)}/star`,
          json("PUT", { starred }),
        )
      ).json();
    },
    async getProject(id: string): Promise<{ project: Project }> {
      return (await request(`/api/projects/${encodeURIComponent(id)}`)).json();
    },
    async updateProject(
      id: string,
      name: string,
      description?: string,
    ): Promise<{ project: Project }> {
      return (
        await request(
          `/api/projects/${encodeURIComponent(id)}`,
          json("PATCH", { name, ...(description === undefined ? {} : {description}) }),
        )
      ).json();
    },
    async listFiles(projectId: string): Promise<{ files: ProjectFile[] }> {
      return (await request(path(projectId))).json();
    },
    async createFile(
      projectId: string,
      file: CreateFile,
    ): Promise<{ file: ProjectFile }> {
      return (await request(path(projectId), json("POST", file))).json();
    },
    async getFile(
      projectId: string,
      fileId: string,
    ): Promise<{ file: ProjectFile }> {
      return (await request(path(projectId, fileId))).json();
    },
    async updateFile(
      projectId: string,
      fileId: string,
      file: UpdateFile,
    ): Promise<{ file: ProjectFile }> {
      return (await request(path(projectId, fileId), json("PUT", file))).json();
    },
    async moveFileToProject(projectId:string,fileId:string,destinationProjectId:string,parentId:string|null,expectedRevision:number):Promise<{file:ProjectFile}>{
      return (await request(path(projectId,fileId),json('PUT',{destinationProjectId,parentId,expectedRevision}))).json();
    },
    async deleteFile(projectId: string, fileId: string): Promise<void> {
      await request(path(projectId, fileId), { method: "DELETE" });
    },
    async saveFile(
      projectId: string,
      fileId: string,
      body: Blob | string | ArrayBuffer,
      expectedRevision: number,
    ): Promise<{ file: ProjectFile }> {
      return (
        await request(`${path(projectId, fileId)}/content`, {
          method: "PUT",
          headers: { "If-Match": `"${expectedRevision}"` },
          body,
        })
      ).json();
    },
    async loadFile(
      projectId: string,
      fileId: string,
    ): Promise<{ bytes: ArrayBuffer; revision: number }> {
      const response = await request(`${path(projectId, fileId)}/content`);
      return {
        bytes: await response.arrayBuffer(),
        revision: Number(response.headers.get("X-File-Revision")),
      };
    },
    async listRevisions(
      projectId: string,
      fileId: string,
    ): Promise<{ revisions: Revision[] }> {
      return (await request(`${path(projectId, fileId)}/revisions`)).json();
    },
    async getRevision(
      projectId: string,
      fileId: string,
      revision: number,
    ): Promise<{ revision: Revision }> {
      return (
        await request(`${path(projectId, fileId)}/revisions/${revision}`)
      ).json();
    },
    async loadRevision(
      projectId: string,
      fileId: string,
      revision: number,
    ): Promise<ArrayBuffer> {
      return (
        await request(
          `${path(projectId, fileId)}/revisions/${revision}/content`,
        )
      ).arrayBuffer();
    },
  };
}
